import { inject, Injectable } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { codeFromTagLink } from '../utils/tag-link';
import { BarcodeMappingService } from './barcode-mapping.service';
import { CheckPointService } from './checkPoint.service';
import { LoggerService } from './logger.service';

/** Where a scanned code points, as router commands. */
export type ScanCodeResolution =
  | {
      found: true;
      /** Which rule matched: a CheckPoint URL, a short-code mapping, or a raw checkpoint ID */
      source: 'url' | 'mapping' | 'checkpoint-id';
      commands: string[];
      extras?: NavigationExtras;
    }
  | { found: false; reason: 'empty' | 'not_found' };

/**
 * Resolves a scanned code — QR text, 1D barcode value, NFC tag payload or tag link
 * (/t/:code) — to the page it should open. One implementation for every input method,
 * all local lookups:
 *
 * 1. CheckPoint URL: /record/:cpId[/:fId[/:instanceId]] or /scan/:cpId
 * 2. Short code in barcode_mappings, bare or in a tag link (printed barcodes and NFC
 *    tags share these codes)
 * 3. Raw checkpoint ID
 *
 * Database failures are thrown (exceptional); an unknown code is a `not_found` result.
 */
@Injectable({ providedIn: 'root' })
export class ScanCodeResolverService {
  private router = inject(Router);
  private barcodeMappingService = inject(BarcodeMappingService);
  private checkPointService = inject(CheckPointService);
  private logger = inject(LoggerService).getLogger('scan-code-resolver');

  /** Where scanned submissions return to on cancel/finish. */
  private readonly returnUrl = '/scan';

  async resolve(scanned: string): Promise<ScanCodeResolution> {
    if (!scanned) return { found: false, reason: 'empty' };
    const code = codeFromTagLink(scanned) ?? scanned;

    const fromUrl = this.resolveUrl(code);
    if (fromUrl) return fromUrl;

    this.logger.debug('Trying short-code mapping lookup for:', code);
    const mapping = await this.barcodeMappingService.getByBarcodeValue(code);
    if (mapping) {
      const commands = ['/submission', mapping.asset_id, mapping.interaction_type_id];
      if (mapping.asset_instance_id) commands.push(mapping.asset_instance_id);
      return { found: true, source: 'mapping', commands, extras: this.returnExtras() };
    }

    this.logger.debug('Treating code as checkpoint ID:', code);
    const checkPoint = await this.checkPointService.getById(code);
    if (checkPoint?.id) {
      return { found: true, source: 'checkpoint-id', commands: ['/submission', checkPoint.id] };
    }

    return { found: false, reason: 'not_found' };
  }

  /** Resolves and, when found, navigates. Returns the resolution either way. */
  async resolveAndNavigate(code: string): Promise<ScanCodeResolution> {
    const resolution = await this.resolve(code);
    if (resolution.found) {
      if (resolution.extras) {
        await this.router.navigate(resolution.commands, resolution.extras);
      } else {
        await this.router.navigate(resolution.commands);
      }
    }
    return resolution;
  }

  private resolveUrl(code: string): ScanCodeResolution | null {
    let path: string;
    try {
      // Only the path matters (labels may carry any origin); a fixed base keeps relative
      // paths like "/scan/cp1" parseable regardless of the page's own origin
      path = new URL(code, 'https://checkpoint.invalid').pathname;
    } catch {
      return null; // Not URL-shaped
    }

    // /scan/ is the current route; /record/ is kept for backward-compat with existing QR codes
    const match = path.match(/^\/(scan|record)\/(.+)$/);
    if (!match) return null;

    const [, prefix, rest] = match;
    const segments = rest.split('/');
    this.logger.debug('Parsed route from URL:', rest);

    if (prefix === 'record' && (segments.length === 2 || segments.length === 3)) {
      // /record/:cpId/:fId[/:instanceId] → submission route, returning to scan on cancel
      return {
        found: true,
        source: 'url',
        commands: ['/submission', ...segments],
        extras: this.returnExtras(),
      };
    }
    // /scan/:cpId or /record/:cpId → choose-form
    return { found: true, source: 'url', commands: ['/' + prefix, ...segments] };
  }

  private returnExtras(): NavigationExtras {
    return { queryParams: { returnUrl: this.returnUrl } };
  }
}
